/**
 * The trainer's cockpit: panel, glareshield, yoke, levers, pedals, and eight live instruments.
 *
 * ## Built in code, not modelled
 *
 * A light aircraft panel is a flat slab with holes in it. Modelling that in a DCC tool and
 * shipping a glTF would add a megabyte to the initial payload (budget: 6 MB gzipped for
 * *everything*) to describe geometry that is a dozen boxes, and it would put the instrument
 * positions somewhere a shader cannot reach. Built here, the panel is ~40 primitives, the gauge
 * quads are placed by the same table that names them, and the whole thing weighs nothing.
 *
 * ## What moves, and why each one
 *
 * A static cockpit is a photograph. Four things move, and all four are things a pilot's eye
 * checks without being asked:
 *
 *   - **The yoke** translates fore and aft with the elevator and rotates with the ailerons. It is
 *     the single strongest cue that the aircraft is being flown — and, on the ground with the
 *     controls free, that it is *not*.
 *   - **The levers** — throttle, mixture, and the flap handle — sit where they have been set.
 *     The flap lever in particular is how a pilot knows what the flaps are doing during the four
 *     seconds they take to travel.
 *   - **The rudder pedals** move with the rudder command, including the auto-coordination
 *     assist's contribution, so the pedals show what the aeroplane is actually being flown with.
 *   - **The needles**, which are the reason any of this exists. See `aircraft/instruments.ts`.
 *
 * ## Glass, and why it is not a reflection probe
 *
 * Instrument glass reflects the cockpit, and the cockpit is dark. What is actually visible on a
 * real panel is a broad, soft sheen across the top third of each dial from whatever light is
 * coming in the windscreen — not a mirror image of anything. So the glass is one additive quad
 * per instrument with a vertical gradient, tinted by the ambient light and angled with the panel.
 * It costs eight transparent quads and it is the difference between the gauges reading as
 * instruments behind glass and as textures on a board.
 *
 * ## The flood light
 *
 * Red, dim, and from above the panel — which is what a real one is, and for a real reason: red
 * light preserves dark adaptation, so a night panel is lit enough to read and no brighter. It is
 * driven by the sun's elevation rather than by a switch, coming up through dusk, and it is the
 * only light in the cockpit: at night the instruments are lit by it and by their own internal
 * lighting, and nothing else in the cabin is visible at all. That is correct and it is also what
 * makes the runway lights out of the window read.
 *
 * `updateFrame(alpha)`: yes — {@link Cockpit.updateFrame} moves the controls and refreshes the
 * instrument textures. Allocation-free per frame.
 */

import {
  BoxGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  CylinderGeometry,
  Group,
  LinearFilter,
  Mesh,
  Object3D,
  PointLight,
  SRGBColorSpace,
  TorusGeometry,
  type BufferGeometry,
  type Material,
  type Texture,
} from 'three';
import { MeshBasicNodeMaterial, MeshStandardNodeMaterial } from 'three/webgpu';
import { disposeCockpitModel, type CockpitModel } from './cockpitModel.js';
import { clamp, expDamp, DEG2RAD } from '../core/math.js';
import {
  buildCockpitShell,
  cabinShapeFor,
  type CabinShape,
  type ShellMaterials,
} from './cockpitShell.js';
import { grainNormal, grainRoughness } from './cockpitSurfaces.js';
import { loadCockpitTextures, type CockpitTextureSet } from './cockpitTextures.js';
import { CockpitEnvironmentMap, type SkyRadiances } from './cockpitEnvironment.js';
import { buildCockpitDetails, type DetailMaterials } from './cockpitDetails.js';
import { placardAtlas } from './cockpitPlacards.js';
import { bakeCabinOcclusion } from './cockpitAmbient.js';
import {
  GAUGE_IDS,
  InstrumentPanel,
  makeInstrumentInputs,
  type GaugeId,
  type GaugeOptions,
  type InstrumentInputs,
} from '../aircraft/instruments.js';

/**
 * Where each gauge sits on the panel, in metres from the panel's centre.
 *
 * The six-pack is the standard T and its layout is not a style choice — it is the arrangement
 * every certificated panel has used since the 1950s, so a pilot's eye already knows where to
 * look. Airspeed, attitude, altimeter across the top; turn coordinator, heading, vertical speed
 * across the bottom. The engine instruments go to the right, out of the scan.
 */
const GAUGE_LAYOUT: Readonly<Record<GaugeId, { x: number; y: number; sizeM: number }>> = Object.freeze({
  airspeed: { x: -0.185, y: 0.095, sizeM: 0.082 },
  attitude: { x: -0.093, y: 0.095, sizeM: 0.082 },
  altimeter: { x: -0.001, y: 0.095, sizeM: 0.082 },
  turn: { x: -0.185, y: 0.003, sizeM: 0.082 },
  heading: { x: -0.093, y: 0.003, sizeM: 0.082 },
  vsi: { x: -0.001, y: 0.003, sizeM: 0.082 },
  tachometer: { x: 0.098, y: 0.088, sizeM: 0.07 },
  fuel: { x: 0.098, y: 0.002, sizeM: 0.07 },
});

/** How far the panel is tilted back from vertical [deg]. */
const PANEL_TILT_DEG = 12;

/**
 * Top of the gauge block, above the panel's own origin [m].
 *
 * Derived from {@link GAUGE_LAYOUT} rather than written down, because it exists to hang that
 * block under an artist panel's top edge and it has to follow the layout if the layout moves.
 */
const GAUGE_BLOCK_TOP_M = Math.max(
  ...Object.values(GAUGE_LAYOUT).map((gauge) => gauge.y + gauge.sizeM / 2),
);

/**
 * How far the cockpit structure sits *below* the pilot's eye [m].
 *
 * The one number that decides whether the aeroplane is flyable from the cockpit, and it was
 * missing. Everything in this file is authored around the panel's own centre; the eye point comes
 * from the aircraft config, and nothing connected the two — so the structure was built level with
 * the pilot's head. Measured, the panel's top edge sat **4.6° above** the horizon and the
 * glareshield and coaming filled +6.1° to +10.8°, leaving a 1.5° slit to see through. The horizon
 * is at 0°. You could not see where you were going.
 *
 * A pilot looks *over* the glareshield. At this offset the glareshield sits about 9° below the
 * eye line, the six-pack spans −15° to −23°, and the horizon is clear in the upper third of the
 * view — which is what the seat height in a 172 is set up to give you.
 */
export const STRUCTURE_BELOW_EYE_M = 0.166;

/** Sun elevation over which the flood light comes up [deg]. */
const FLOOD_FULL_AT_DEG = -6;
const FLOOD_OFF_AT_DEG = 4;

/**
 * What a sunlit instrument panel is worth on screen, after exposure.
 *
 * Every brightness in this file is *exposure-referred* — divided by the scene's auto-exposure so
 * that the rendered result is this number whatever the world outside is doing. It has to be: the
 * gauge faces are unlit materials writing straight into an HDR buffer that is later multiplied by
 * an exposure which climbs to 48× at night.
 */
const DAY_PANEL_LUMINANCE = 0.55;

/**
 * Gain on the flood-lit half of an instrument face.
 *
 * Derived rather than dialled: at full flood the face tint is (0.62, 0.16, 0.10), so a gain of
 * `DAY_PANEL_LUMINANCE / 0.62` lands the red channel — the one you actually read a night panel by
 * — on the same rendered luminance as a sunlit face. A night instrument then reads as *the same
 * brightness, a different colour*, which is what a red-flooded panel actually looks like.
 *
 * It was 2.6, which put the red channel at 1.61 — blown, before the texture was even applied. That
 * survived because the daylight term was not exposure-referred either, so the two errors were
 * compared against each other and both looked plausible at noon. Fixing only the daylight term
 * left this one dominant through the whole of dusk, which is exactly when a pilot needs the panel.
 */
const FLOOD_FACE_GAIN = DAY_PANEL_LUMINANCE / 0.62;

/** The six-pack alone: the standard T, and every aeroplane here has it. */
const SIX_PACK: readonly GaugeId[] = ['airspeed', 'attitude', 'altimeter', 'turn', 'heading', 'vsi'];

/**
 * What makes one aeroplane's cockpit different from another's.
 *
 * Until v1.1 there was **one cockpit for all three aircraft**, and it was the trainer's. A pilot
 * flying the FX-16 — a fly-by-wire fighter — sat behind a Cessna 172 panel, hands on a control
 * yoke, next to a red mixture lever, reading a fuel gauge marked `US GAL` and a tachometer
 * labelled `RPM / HUNDREDS`. `CockpitOptions` had no aircraft id, so there was nothing to vary.
 *
 * This is that id, in the only form worth having: **derived from the aircraft's own data**, so
 * `cockpit.ts` still contains no per-aircraft branch and non-negotiable #8 survives.
 */
export interface CockpitProfile {
  /** What the pilot's right hand is on. */
  control: 'yoke' | 'stick';
  /** Which gauges this panel carries. */
  gauges: readonly GaugeId[];
  /** Panel face colour. */
  panelColor: number;
  /**
   * Sidewall, headliner and bulkhead colour.
   *
   * Separate from {@link panelColor} because on every real aeroplane they *are* separate: the
   * panel is dark so instruments read against it and so it does not reflect into the windscreen,
   * and the cabin around it is lighter so the interior is not a cave. Making them one colour was
   * the first thing tried and it produced a black box with a black panel in it.
   */
  cabinColor: number;
}

/**
 * Read the cockpit's character out of the flight model's own numbers.
 *
 * Two properties decide it, and both are already in every aircraft config:
 *
 * **The control.** A jet fighter has a stick, and so does anything cleared for sustained inverted
 * flight — you cannot fly a competition sequence with a yoke, which is why no aerobat has one.
 * `gLimitNegative` is the honest discriminator: the trainer is −1.52 g, the aerobat −10, and an
 * aeroplane rated to hang on its straps at −10 g is an aeroplane with a stick between the pilot's
 * knees. The fighter is caught by its engine before the g test is reached.
 *
 * **The panel.** A turbofan has no crankshaft and no gallons: a piston tachometer reading
 * `RPM / HUNDREDS` and a fuel gauge marked `US GAL` are not mislabelled on a jet, they are the
 * wrong instruments. The fighter therefore carries the six standby instruments and nothing else,
 * which is both correct for the type and honest about what this simulator models — its primary
 * flight information is already on the HUD (`aircraft/fighterHud.ts`).
 */
export function cockpitProfileFor(config: {
  engine: { kind?: string };
  limits: { gLimitNegative: number };
}): CockpitProfile {
  const isJet = config.engine.kind === 'turbofan';
  const isAerobatic = config.limits.gLimitNegative <= -4;
  return {
    control: isJet || isAerobatic ? 'stick' : 'yoke',
    gauges: isJet ? SIX_PACK : GAUGE_IDS,
    // A trainer's panel is light grey plastic; a fighter's is near-black so a HUD reads against
    // it; an aerobat sits between the two.
    panelColor: isJet ? 0x22262b : isAerobatic ? 0x2f343b : 0x3a4048,
    // The cabin is lighter than its panel in every case, and by more in the trainer: a light
    // aeroplane's interior is grey vinyl, an aerobat's is painted alloy, a fighter's is the
    // grey-green everything military has been since about 1955.
    cabinColor: isJet ? 0x8e9a86 : isAerobatic ? 0x9ca4ae : 0xb6bdc6,
  };
}

/** The trainer's cockpit, which is what this file builds when nothing says otherwise. */
export const DEFAULT_COCKPIT_PROFILE: CockpitProfile = Object.freeze({
  control: 'yoke',
  gauges: GAUGE_IDS,
  panelColor: 0x3a4048,
  cabinColor: 0xb6bdc6,
});

export interface CockpitOptions {
  /** Marking data for the airspeed indicator and tachometer faces. */
  gauges: GaugeOptions;
  /** The pilot's eye in body axes [m], so the panel is placed relative to it. */
  eyePointM: readonly [number, number, number];
  /** What this aeroplane's cockpit looks like. Defaults to the trainer's. */
  profile?: CockpitProfile;
}

/** What the cockpit reads each frame. */
export interface CockpitControls {
  /** Elevator command, −1…1. */
  pitch: number;
  /** Aileron command, −1…1. */
  roll: number;
  /** Rudder command including the assist, −1…1. */
  yaw: number;
  /** Throttle lever, 0…1. */
  throttle: number;
  /** Mixture lever, 0…1. 1 is full rich. */
  mixture: number;
  /** Flap angle [deg]. */
  flapAngleDeg: number;
  /** Maximum flap angle [deg], so the lever's travel is the aircraft's. */
  maxFlapDeg: number;
}

export function makeCockpitControls(): CockpitControls {
  return { pitch: 0, roll: 0, yaw: 0, throttle: 0, mixture: 1, flapAngleDeg: 0, maxFlapDeg: 30 };
}

export class Cockpit {
  /**
   * The cockpit, in body-relative render space. Parent it to the aircraft group.
   *
   * A child of the aircraft rather than positioned each frame from the flight state: the panel is
   * rigidly attached to the airframe, so the scene graph is the right place to say so, and it
   * means the cockpit inherits the interpolated pose for free rather than needing its own copy of
   * the interpolation.
   */
  readonly group = new Group();

  readonly instruments: InstrumentPanel;
  private readonly inputs: InstrumentInputs = makeInstrumentInputs();

  private readonly textures = new Map<GaugeId, CanvasTexture>();
  /**
   * Clones of the photographed maps, one per material that uses one.
   *
   * Held because `repeat` is a property of the texture, not of the material, so two surfaces
   * tiling the same source at different rates need two texture objects — and because a clone is
   * a GPU upload of its own and has to be disposed with the cockpit.
   */
  private readonly textureSwaps: Texture[] = [];

  /**
   * The sky the cabin reflects — see `cockpitEnvironment.ts`.
   *
   * Owned here rather than by the scene because it is assigned per *material*: putting it on
   * `Scene.environment` would apply image-based lighting to the terrain, the water and the
   * aircraft too, which is a change to the whole simulator's look made while working on a panel.
   */
  private readonly environmentMap = new CockpitEnvironmentMap();

  /** Every lit material, so the environment map reaches all of them. */
  private readonly litMaterials: MeshStandardNodeMaterial[] = [];
  private readonly geometries: BufferGeometry[] = [];
  private readonly materials: Material[] = [];

  /** What this aeroplane's cockpit looks like. See {@link cockpitProfileFor}. */
  private readonly profile: CockpitProfile;

  /**
   * Half the cabin's internal width [m], from the shell.
   *
   * Held here so the panel is sized *from* the enclosure rather than beside it — the two
   * disagreeing by 20 cm is exactly the gap the deleted side posts were papering over.
   */
  private cabinHalfWidthM = 0.52;

  /** The cabin, kept so the furniture can be laid out against its real dimensions. */
  private cabinShape: CabinShape | null = null;

  /**
   * The primary control, animated by the pilot's inputs.
   *
   * `yoke` is the outer group — it slides fore and aft with pitch on a yoke, and tilts with pitch
   * on a stick. `yokeWheel` is the part that answers roll: the wheel that turns on a yoke, and
   * nothing on a stick, where roll tilts the whole column instead. Two groups either way, so
   * {@link updateFrame} does not care which is fitted.
   */
  private readonly yoke = new Group();
  private readonly yokeWheel = new Group();
  private readonly throttleLever = new Group();
  private readonly mixtureLever = new Group();
  private readonly flapLever = new Group();
  private readonly pedalLeft = new Group();
  private readonly pedalRight = new Group();

  /**
   * The panel's own tilted frame.
   *
   * Everything mounted *on* the panel is a child of this rather than positioned in the cockpit's
   * frame, and that is a bug fix rather than tidiness: the panel is tilted 12° back, so a gauge
   * placed at a fixed `z` sits progressively further behind the panel's surface the lower down it
   * is. Measured, the six-pack's bottom row was 5 mm *inside* the panel and invisible while the
   * top row was 14 mm proud of it — which looked exactly like four instruments failing to draw.
   */
  private readonly panelFace = new Group();

  /**
   * Everything that is *not* the pilot's head, dropped to where a cockpit actually sits.
   *
   * A single group rather than 0.166 subtracted from twenty hard-coded offsets: the offsets
   * describe the cockpit's own internal layout and are right, and what was wrong was one
   * relationship — where that layout sits relative to the eye. See {@link STRUCTURE_BELOW_EYE_M}.
   */
  private readonly structure = new Group();

  /**
   * The procedural parts, split by what an artist cockpit would replace.
   *
   * `proceduralGauges` is listed but never hidden — it exists so the split is exhaustive and the
   * reader can see that the live instruments were considered and deliberately kept.
   */
  private readonly proceduralCabin: Object3D[] = [];
  private readonly proceduralPanel: Object3D[] = [];
  private readonly proceduralGauges: Object3D[] = [];
  private readonly claimed = new Set<Object3D>();
  /** Each live instrument's group, so an artist panel can say where it goes. */
  private readonly gaugeHolders = new Map<GaugeId, Group>();
  private model: CockpitModel | null = null;

  private readonly floodLight: PointLight;
  /** Instrument self-illumination, so the faces are readable at night without being lit. */
  private readonly gaugeMaterials: MeshBasicNodeMaterial[] = [];
  private readonly glassMaterials: MeshBasicNodeMaterial[] = [];

  /**
   * What the occlusion bake cost this cockpit [ms]. Published, and that is the point.
   *
   * `cockpitAmbient.test.ts` can only bake the *shell* — the panel needs a DOM canvas for its
   * instruments, so a unit test cannot build a whole cockpit — and the shell is four meshes out of
   * fifty-one. That test therefore measured a fraction of the real work and passed comfortably at
   * 30 ms while the real thing took **497**, which is how a half-second of start-up shipped
   * unnoticed. A number nobody can read is a number nobody checks, so this one is on the debug
   * handle where a probe can reach it.
   */
  occlusionBakeMs = 0;

  /** Smoothed control positions — a yoke that snapped would look like a solenoid. */
  private smoothPitch = 0;
  private smoothRoll = 0;
  private smoothYaw = 0;
  private floodAmount = 0;
  private visibleValue = false;

  constructor(options: CockpitOptions) {
    this.profile = options.profile ?? DEFAULT_COCKPIT_PROFILE;
    this.instruments = new InstrumentPanel(options.gauges);
    this.group.name = 'cockpit';
    this.group.visible = false;

    this.structure.position.y = -STRUCTURE_BELOW_EYE_M;
    this.group.add(this.structure);

    const eye = options.eyePointM;
    /*
     * The panel sits forward of and below the eye.
     *
     * Body axes are FRD, render axes are x right / y up / z back — so the eye point converts as
     * `(y, −z, −x)`. The panel is then 0.72 m further forward and 0.30 m down from there, which
     * is a real 172's geometry: the top of the panel is just below the pilot's sight line over
     * the glareshield, and the six-pack sits comfortably in the lower half of the view.
     */
    this.group.position.set(eye[1], -eye[2], -eye[0]);

    /*
     * Each build step's output is claimed as it is made, so an artist cockpit can switch off
     * exactly the part it replaces.
     *
     * Recorded here rather than looked up later by name or by type, because by the time the model
     * arrives there is no way to tell the coaming from the canopy bow: everything is a `Mesh` on
     * one group. Claiming at the moment of construction is the only point where the answer is
     * known for free. The gauges are their own list and are never switched off — they are the
     * live `InstrumentPanel`, and no artist asset supplies a moving airspeed needle.
     */
    this.buildShell();
    this.claim(this.proceduralCabin);
    this.buildPanel();
    this.claim(this.proceduralPanel);
    this.buildGauges();
    this.claim(this.proceduralGauges);
    this.buildYoke();
    this.claim(this.proceduralCabin);
    this.buildLevers();
    this.claim(this.proceduralCabin);
    this.buildPedals();
    this.claim(this.proceduralCabin);
    this.buildDetails();
    this.claim(null);

    /*
     * The flood light: red, above and behind the panel's top edge, pointing down at it.
     *
     * A `PointLight` with a short range rather than a spot, because the thing being lit is
     * 60 cm wide and 25 cm away and a cone would need a penumbra to avoid a hard edge across the
     * middle of the panel. The decay does that for free.
     */
    this.floodLight = new PointLight(new Color(0xff2f18), 0, 1.4, 2);
    this.floodLight.name = 'cockpit-flood';
    this.floodLight.position.set(0, 0.30, -0.52);
    this.floodLight.castShadow = false;
    this.structure.add(this.floodLight);

    /*
     * Occlusion, baked last, over **everything** — cabin, panel, coaming, yoke, levers, pedals.
     *
     * Last because every piece has to be able to shadow every other piece, and the interesting
     * shadows are between the groups rather than inside them: the glareshield darkening the top of
     * the panel, the panel darkening the footwell, the yoke sitting against the six-pack. Baking
     * inside `buildCockpitShell` covered the cabin alone, which left the single largest surface a
     * pilot looks at — the instrument panel — as flat as it was before any of this.
     *
     * `updateMatrixWorld` first, because the parts are placed by transform rather than baked into
     * their vertices, and the baker reads world positions. See the note in `TriangleGrid`.
     */
    this.group.updateMatrixWorld(true);
    const occluders: Mesh[] = [];
    this.group.traverse((object) => {
      if (object instanceof Mesh) occluders.push(object);
    });
    this.occlusionBakeMs = bakeCabinOcclusion(occluders);
  }

  /**
   * Record everything built since the last call, so it can be switched off selectively later.
   *
   * `into === null` splits by parent instead: `buildDetails` is the one step that adds to both the
   * panel and the cabin, and where an object was parented is exactly the distinction wanted.
   */
  private claim(into: Object3D[] | null): void {
    for (const object of [...this.structure.children, ...this.panelFace.children]) {
      if (this.claimed.has(object)) continue;
      this.claimed.add(object);
      if (into !== null) into.push(object);
      else if (object.parent === this.panelFace) this.proceduralPanel.push(object);
      else this.proceduralCabin.push(object);
    }
  }

  /**
   * Adopt an artist cockpit, switching off only the procedural parts it actually replaces.
   *
   * The asset says what it supplies and this obeys it — see `cockpitModel.ts`. The live
   * instruments are never switched off; when the model brings its own panel they are moved onto
   * it, using the distance the model itself was measured at, so the needles land on the artist's
   * bezels rather than floating in front of them.
   *
   * Idempotent in the sense that matters: a second call replaces the first model. It cannot be
   * reached with a failed load, because `loadCockpitModel` resolves `null` rather than throwing
   * and the caller simply never gets here.
   */
  adoptModel(model: CockpitModel): void {
    if (this.model !== null) {
      this.group.remove(this.model.group);
      disposeCockpitModel(this.model);
    }
    this.model = model;
    /*
     * Parented to `group`, **not** to `structure`, and the difference is 0.166 m of aeroplane.
     *
     * `structure` is deliberately dropped by {@link STRUCTURE_BELOW_EYE_M} because everything in
     * this file is authored around the panel's own centre and had to be related to the eye. An
     * artist cockpit has no such problem: `prep-cockpits.py` puts the pilot's eye at the model's
     * origin, so it is already eye-relative and adding it to `structure` applies that correction a
     * second time. Measured in the running game, that sank the Cessna's glareshield to 20° below
     * the sight line — a panel you look over the top of rather than at.
     */
    this.group.add(model.group);

    if (model.supplies.includes('cabin')) {
      for (const object of this.proceduralCabin) object.visible = false;
    }
    if (model.supplies.includes('panel')) {
      for (const object of this.proceduralPanel) object.visible = false;
      /*
       * Move the live instruments onto the artist's panel, in the artist's frame.
       *
       * The same reparent as above and for the same reason: the anchor was measured from the eye,
       * and `panelFace` hangs off `structure`, so leaving it there would place the gauges 0.166 m
       * below the face they are meant to sit on. Reparenting to `group` puts the measurement and
       * the placement in one frame.
       *
       * 25 mm proud of the face is the depth of the bezel rings the gauges are drawn in: any less
       * and the faces z-fight with the panel behind them, any more and they read as floating. The
       * tilt stays as built — `PANEL_TILT_DEG` is how far a real instrument panel leans back, and
       * that is a property of panels rather than of any one model.
       */
      /*
       * An artist panel that names its own instrument positions gets them, one by one.
       *
       * This is the same inversion as the exterior's `NOZZLE` anchor: the asset states where its
       * things are and the runtime follows, rather than the runtime holding a layout every asset
       * has to agree with. `GAUGE_LAYOUT` is a certificated six-pack — three over three with the
       * engine instruments right — and the Extra's panel is four over four with a display between
       * the middle pair. Neither is wrong; they are different aeroplanes. Placed by anchor, eight
       * live gauges land in eight artist bezels.
       *
       * The holders are reparented to `group` because the anchors were measured from the eye, and
       * `panelFace` carries the procedural panel's own offset and tilt. Each keeps its built
       * rotation so the faces still lean back the way an instrument panel does.
       */
      if (model.gaugeAnchors.size > 0) {
        for (const [id, anchor] of model.gaugeAnchors) {
          const holder = this.gaugeHolders.get(id);
          if (holder === undefined) continue;
          this.group.add(holder);
          holder.position.copy(anchor);
          // Proud of the artist's own bezel face, by the depth of the ring the gauge is drawn in.
          holder.position.z += 0.012;
          holder.rotation.x = -PANEL_TILT_DEG * DEG2RAD;
        }
      } else if (model.panel !== null) {
        this.group.add(this.panelFace);
        this.panelFace.position.x = model.panel.position.x;
        /*
         * Hung from the *top* of the artist face, not centred on its mean. See `PanelAnchor.top`
         * — the mean is dragged down by whatever console a model happens to have below the panel,
         * and a six-pack goes in the upper half of a panel on every aeroplane there is. A 30 mm
         * margin keeps the top row inside the artist's own bezel surround rather than straddling
         * its upper edge.
         */
        this.panelFace.position.y = model.panel.top.y - GAUGE_BLOCK_TOP_M - 0.03;
        /*
         * Depth comes from the *nearest* hit, not from the mean the other two axes use.
         *
         * A panel is not a plane. On the Extra the mean face is 0.452 m out and the nearest
         * pilot-facing surface in the same cone is 0.344 — a 10 cm lip — so instruments placed at
         * the mean sit behind it and the aircraft flew with eight empty bezels. Anything further
         * back than the nearest qualifying surface is occluded from at least one angle in the
         * cone, so the nearest hit is the only depth that is guaranteed visible. Slightly proud is
         * a small cost; invisible is not a cost, it is a missing instrument panel.
         */
        this.panelFace.position.z = -(model.panel.distanceM - 0.025);
      }

    }
  }

  /** The artist cockpit in use, or null when this is the procedural fallback. For probes. */
  get artistModel(): CockpitModel | null {
    return this.model;
  }

  /** Show or hide the whole cockpit — it is only drawn in the cockpit view. */
  setVisible(visible: boolean): void {
    this.visibleValue = visible;
    this.group.visible = visible;
  }

  get visible(): boolean {
    return this.visibleValue;
  }

  private track<T extends BufferGeometry>(geometry: T): T {
    this.geometries.push(geometry);
    return geometry;
  }

  private trackMaterial<T extends Material>(material: T): T {
    this.materials.push(material);
    return material;
  }

  /**
   * A lit cockpit surface, tracked for disposal and already reading the baked occlusion.
   *
   * A factory rather than a line repeated fourteen times, because the failure mode of forgetting
   * `vertexColors` is silent: the attribute is still computed, still uploaded, and simply ignored,
   * which renders as exactly the flat interior the occlusion pass exists to fix. The unlit
   * materials — the gauge faces and their glass — deliberately do *not* go through here. An
   * instrument is internally lit, so shading it by how enclosed the cockpit is around it would be
   * both wrong and, at night, unreadable.
   */
  /**
   * @param occluded Whether this surface is large enough for baked occlusion to *mean* anything.
   *   False for the small hard parts — switch levers, breaker pins, instrument bezels, the yoke.
   *   Vertex occlusion is interpolated across a triangle, so on a 2 cm ring there is nothing to
   *   interpolate: the value is uniform, indistinguishable from just choosing a slightly darker
   *   colour, and it is not free. Those parts still *occlude* — a bezel absolutely does shadow the
   *   panel around it — they simply do not carry a term of their own.
   *
   *   This is the same argument as `aoSegments` in `cockpitShell.ts`, applied one level up:
   *   subdivide where the term can vary, and do not compute it where it cannot.
   */
  private litMaterial(occluded = true): MeshStandardNodeMaterial {
    const material = this.trackMaterial(new MeshStandardNodeMaterial());
    material.vertexColors = occluded;
    /*
     * The environment map is assigned here, once, and the texture is mutated in place afterwards.
     *
     * Assigning it later would mean setting `needsUpdate` and recompiling the shader every time
     * the sky moved; assigning it now means the material is built with an `EnvironmentNode` from
     * the start and the sun can cross the sky without touching a pipeline.
     */
    material.envMap = this.environmentMap.texture;
    material.envMapIntensity = 1;
    this.litMaterials.push(material);
    return material;
  }

  /**
   * Hand the cabin the sky it is flying under. Called from the scene's environment update.
   *
   * Cheap to call every frame: {@link CockpitEnvironmentMap.setSky} returns immediately unless the
   * sun has actually moved, because rewriting the texture makes three regenerate its PMREM chain.
   */
  setSky(sky: SkyRadiances): void {
    this.environmentMap.setSky(sky);
  }

  /**
   * The cabin around the pilot — see `cockpitShell.ts` for what and why.
   *
   * Built before the panel so the panel's own geometry sits *inside* an enclosure that already
   * exists, which is the order the numbers were derived in and the order that makes a mistake
   * visible: a panel wider than its cabin is obvious immediately, and a panel floating in space
   * is what this replaces and was not obvious for four milestones.
   */
  private buildShell(): void {
    const shape = cabinShapeFor({
      control: this.profile.control,
      gaugeCount: this.profile.gauges.length,
    });
    this.cabinHalfWidthM = shape.halfWidthM;
    this.cabinShape = shape;

    /*
     * Four materials, and the grain maps are the point of them.
     *
     * A constant roughness renders as a perfectly uniform surface, which is a thing that does not
     * exist, and the eye reads it as plastic instantly. Each of these carries a procedural
     * roughness map and a normal map derived from *the same* noise field, so the dull patches and
     * the bumps are the same features rather than two effects stacked on one surface. See
     * `cockpitSurfaces.ts`.
     */
    const interior = this.litMaterial();
    interior.color = new Color(this.profile.cabinColor);
    interior.roughness = 0.95;
    interior.metalness = 0;
    interior.roughnessMap = grainRoughness('vinyl', 6);
    interior.normalMap = grainNormal('vinyl', 6);
    interior.normalScale.set(0.6, 0.6);

    const trim = this.litMaterial();
    trim.color = new Color(0x3a4048);
    trim.roughness = 0.9;
    trim.metalness = 0.05;
    trim.roughnessMap = grainRoughness('vinyl', 4);
    trim.normalMap = grainNormal('vinyl', 4);
    trim.normalScale.set(0.4, 0.4);

    /*
     * The one metallic surface, and it is metallic on purpose.
     *
     * There is no environment map in this scene, so a metal here has nothing to reflect and
     * renders as **black**. This was first written at `metalness: 0.55` with a comment predicting
     * exactly that failure and asserting 0.55 was low enough to avoid it; the screenshot showed
     * the windscreen pillars as black girders across the view, so it was not. A painted alloy
     * frame is mostly a *dielectric* anyway — it is the paint you are looking at, not the metal
     * under it — so the honest value is near zero and the character comes from the roughness map.
     */
    const metal = this.litMaterial();
    metal.color = new Color(0xa8afb8);
    metal.roughness = 0.85;
    metal.metalness = 0.12;
    metal.roughnessMap = grainRoughness('metal', 3);
    metal.normalMap = grainNormal('metal', 3);
    metal.normalScale.set(0.25, 0.25);

    const seat = this.litMaterial();
    seat.color = new Color(0x31353c);
    seat.roughness = 0.88;
    seat.metalness = 0;
    seat.roughnessMap = grainRoughness('leather', 2);
    seat.normalMap = grainNormal('leather', 2);
    seat.normalScale.set(0.9, 0.9);

    const materials: ShellMaterials = { interior, trim, metal, seat };
    const shell = buildCockpitShell(shape, materials);
    for (const geometry of shell.geometries) this.geometries.push(geometry);
    this.structure.add(shell.group);

    /*
     * The photographed maps, when and if they arrive.
     *
     * Everything above is already complete and correct — the procedural grain is applied, the
     * cockpit renders. This *upgrades* it, asynchronously, and a fetch that never lands leaves a
     * slightly plainer cabin rather than an untextured one. See `cockpitTextures.ts`.
     */
    void loadCockpitTextures().then((textures) => {
      this.applyPhotographedSurfaces({ interior, trim, metal, seat }, textures);
    });
  }

  /**
   * Swap procedural grain for photographed grain on the four cabin surfaces.
   *
   * Each map is applied only if it loaded, so a partial fetch gives a partly-upgraded cabin rather
   * than an inconsistent one — the two grains are both plausible surfaces, so a wall with one and
   * a seat with the other looks like two materials, which is what they are.
   *
   * The repeats are in *tiles across the source square*, and they are the one number here that is
   * a judgement: the vinyl sample is about 40 cm of real hide, so six repeats across a metre of
   * sidewall puts the pebble at roughly life size, and the seat gets two because a seat cushion is
   * about half the sample.
   */
  private applyPhotographedSurfaces(
    materials: ShellMaterials,
    textures: Partial<CockpitTextureSet>,
  ): void {
    const { interior, trim, metal, seat } = materials as {
      interior: MeshStandardNodeMaterial;
      trim: MeshStandardNodeMaterial;
      metal: MeshStandardNodeMaterial;
      seat: MeshStandardNodeMaterial;
    };

    /*
     * `normalScale` is per surface, and it is the number this whole exercise turned on.
     *
     * The materials were built with procedural noise whose relief is deliberately faint, so their
     * scales were 0.25–0.9. Applied unchanged to a *photographed* normal map those render as
     * nothing at all: two commits went by with the maps loading, binding and being invisible, and
     * the environment map — which is genuinely also required — was not the whole story. Setting
     * the scale to a deliberately absurd 4 was what proved the path worked end to end; these are
     * that experiment brought back to something a cockpit looks like.
     */
    for (const [material, normal, roughness, repeat, scale] of [
      [interior, textures.vinylNormal, textures.vinylRoughness, 4, 1.5],
      [trim, textures.vinylNormal, textures.vinylRoughness, 3, 1.1],
      [metal, textures.metalNormal, textures.metalRoughness, 3, 1],
      [seat, textures.leatherNormal, textures.leatherRoughness, 2, 1.4],
    ] as const) {
      /*
       * Cloned per material, because `repeat` lives on the *texture* rather than on the material.
       * Sharing one instance between the sidewall at six tiles and the console trim at four would
       * silently give both whichever was assigned last — and both would still look like a
       * textured surface, which is why this is a clone rather than a comment.
       */
      if (normal !== undefined) {
        const map = normal.clone();
        map.repeat.set(repeat, repeat);
        map.needsUpdate = true;
        material.normalMap = map;
        material.normalScale.set(scale, scale);
        this.textureSwaps.push(map);
      }
      if (roughness !== undefined) {
        const map = roughness.clone();
        map.repeat.set(repeat, repeat);
        map.needsUpdate = true;
        material.roughnessMap = map;
        this.textureSwaps.push(map);
      }
      material.needsUpdate = true;
    }

    // The seat is the one surface whose own colour is worth having: leather reads as leather
    // largely through its colour variation, and a flat tint plus a normal map does not.
    if (textures.leatherDiffuse !== undefined) {
      const map = textures.leatherDiffuse.clone();
      map.repeat.set(2, 2);
      map.needsUpdate = true;
      seat.map = map;
      // Lightened, because the map is now carrying most of the darkness the flat colour used to.
      seat.color = new Color(0x8a8378);
      seat.needsUpdate = true;
      this.textureSwaps.push(map);
    }
  }

  /**
   * Switches, breakers, placards, the trim wheel and the harness — see `cockpitDetails.ts`.
   *
   * Built after the panel because it is laid out against the panel's real width, and before the
   * occlusion bake at the end of the constructor because every one of these is a small hard object
   * that should be sitting in its own contact shadow.
   */
  private buildDetails(): void {
    const shape = this.cabinShape;
    if (shape === null) return;

    const plastic = this.litMaterial(false);
    plastic.color = new Color(0x14171b);
    plastic.roughness = 0.55;
    plastic.metalness = 0.05;

    /*
     * Bright, and much shinier than the cabin's frame.
     *
     * A toggle lever and a breaker pin are bare polished metal that hands have been on for years,
     * and they are the only things in a cockpit that genuinely glint. Now that there is a sky to
     * reflect (see `cockpitEnvironment.ts`) a low roughness finally buys something — this is the
     * surface that shows the environment map is doing its job.
     */
    const metal = this.litMaterial(false);
    metal.color = new Color(0xc9ced6);
    metal.roughness = 0.28;
    metal.metalness = 0.5;

    const webbing = this.litMaterial(false);
    webbing.color = new Color(0x3d3f45);
    webbing.roughness = 0.95;
    webbing.metalness = 0;

    /*
     * Unlit, and not through {@link litMaterial}: a placard is white paint on a dark plate, and
     * what keeps it legible at dusk is the paint, not the light falling on it. Shading it by how
     * enclosed the cockpit is would put the switch labels into shadow at exactly the hour a pilot
     * starts looking for them.
     */
    const placard = this.trackMaterial(new MeshBasicNodeMaterial());
    placard.map = placardAtlas();
    placard.transparent = true;
    placard.depthWrite = false;

    const materials: DetailMaterials = { plastic, metal, webbing, placard };
    const details = buildCockpitDetails(
      shape,
      { panelHalfWidthM: this.cabinHalfWidthM - 0.03, control: this.profile.control },
      materials,
    );
    for (const geometry of details.geometries) this.geometries.push(geometry);
    this.panelFace.add(details.panelGroup);
    this.structure.add(details.cabinGroup);
  }

  /** The panel slab, the glareshield above it, and the coaming that hides its top edge. */
  private buildPanel(): void {
    const panelMaterial = this.litMaterial();
    panelMaterial.color = new Color(this.profile.panelColor);
    panelMaterial.roughness = 0.82;
    panelMaterial.metalness = 0.05;

    const trimMaterial = this.litMaterial();
    trimMaterial.color = new Color(0x15181c);
    trimMaterial.roughness = 0.95;

    // The panel: 0.62 m wide, 0.34 m tall, tilted back so it faces the pilot.
    this.panelFace.position.set(0, -0.11, -0.66);
    this.panelFace.rotation.x = -PANEL_TILT_DEG * DEG2RAD;
    this.structure.add(this.panelFace);

    /*
     * The panel spans the cabin, because a real one does.
     *
     * It was 0.62 m wide inside a cabin that is now 1.04, which left a hand's width of daylight
     * between each end of the panel and the sidewall — the gap the two floating "side posts"
     * below used to half-hide. A panel is a bulkhead: it goes wall to wall, and the instruments
     * occupy the third of it in front of the pilot.
     */
    const panelWidth = this.cabinHalfWidthM * 2 - 0.06;
    const panel = new Mesh(this.track(new BoxGeometry(panelWidth, 0.34, 0.02)), panelMaterial);
    this.panelFace.add(panel);

    // The glareshield: an overhanging lip that shades the panel and frames the view.
    const glareshield = new Mesh(
      this.track(new BoxGeometry(panelWidth + 0.05, 0.02, 0.17)),
      panelMaterial,
    );
    glareshield.position.set(0, 0.075, -0.60);
    glareshield.rotation.x = 7 * DEG2RAD;
    this.structure.add(glareshield);

    /*
     * A padded coaming along the glareshield's leading edge — the roll a real one has.
     *
     * Its own material rather than the shared trim: it is the one surface in the cockpit in direct
     * sunlight through the windscreen, so at the scene's exposure a semi-glossy trim finish blew
     * out to a white bar across the whole view. A real glareshield is deliberately matte black for
     * the same reason a pilot would not want that either.
     */
    const coamingMaterial = this.litMaterial();
    coamingMaterial.color = new Color(0x0a0c0e);
    coamingMaterial.roughness = 1;
    coamingMaterial.metalness = 0;
    const coaming = new Mesh(
      this.track(new CylinderGeometry(0.022, 0.022, panelWidth + 0.05, 10)),
      coamingMaterial,
    );
    coaming.rotation.z = Math.PI / 2;
    coaming.position.set(0, 0.078, -0.525);
    this.structure.add(coaming);

    /*
     * The two floating side posts are gone.
     *
     * They existed to stop the panel ending in mid-air at the edges of the field of view, which
     * was a real problem and this was not a fix for it: they were 0.5 m boxes that themselves
     * ended in mid-air, above and below, so all they did was put two more free-floating objects
     * either side of a free-floating panel. The cabin's windscreen pillars now do the job the
     * posts were standing in for, and they do it by joining two things.
     */

    // The sub-panel below: switches and the radio stack, as blocked-in shapes. Detail here is
    // wasted — it is below the pilot's scan and behind the yoke.
    const subPanel = new Mesh(this.track(new BoxGeometry(panelWidth, 0.14, 0.02)), trimMaterial);
    subPanel.position.set(0, -0.30, -0.62);
    subPanel.rotation.x = -PANEL_TILT_DEG * DEG2RAD;
    this.structure.add(subPanel);

    /*
     * The right-hand side of the panel, now that there is one.
     *
     * A widened panel is a widened *empty* panel unless something occupies it: the gauge layout
     * puts every instrument in the left third, in front of the pilot, which is correct and leaves
     * two thirds bare. A radio stack and a blanking plate are what is actually there on the
     * aeroplanes being modelled, and blocked-in shapes are enough — this is 40° off the scan and
     * the eye only needs to know the space is *occupied*.
     */
    const stackMaterial = this.litMaterial();
    stackMaterial.color = new Color(0x101317);
    stackMaterial.roughness = 0.6;
    stackMaterial.metalness = 0.25;
    const stack = new Mesh(this.track(new BoxGeometry(0.15, 0.26, 0.012)), stackMaterial);
    stack.position.set(0.235, 0.045, 0.017);
    this.panelFace.add(stack);
    for (let i = 0; i < 4; i++) {
      const face = new Mesh(this.track(new BoxGeometry(0.13, 0.038, 0.006)), trimMaterial);
      face.position.set(0.235, 0.135 - i * 0.058, 0.024);
      this.panelFace.add(face);
    }
  }

  /**
   * One quad per instrument, plus one glass quad over each.
   *
   * `MeshBasicNodeMaterial` for the faces: an instrument is internally lit, so it must not go
   * black when the cockpit does. What varies with the light is the *brightness* of that internal
   * lighting, which is a colour multiply rather than a lighting calculation.
   */
  private buildGauges(): void {
    const bezelMaterial = this.litMaterial(false);
    bezelMaterial.color = new Color(0x0b0d10);
    bezelMaterial.roughness = 0.6;
    /*
     * Metalness 0, not 0.35, and that is not a downgrade.
     *
     * There is no environment map anywhere in this scene — `grep` for `PMREM`, `envMap` or
     * `scene.environment` returns nothing. A metallic surface gets its appearance almost entirely
     * from what it reflects, so 0.35 metalness here surrendered 35 % of the diffuse response and
     * got back only a direct sun highlight: the bezels rendered darker and flatter than the flat
     * colour they were given, which is the opposite of the intent. This is the same defect M7.5
     * found and fixed on the exterior (cc5766e) and never came back for in here.
     *
     * The metal read now comes from roughness and the specular highlight instead. If the scene
     * ever grows a real environment probe from the atmosphere model, this is the line to revisit.
     */
    bezelMaterial.metalness = 0;

    for (const id of this.profile.gauges) {
      const layout = GAUGE_LAYOUT[id];
      const texture = new CanvasTexture(this.instruments.canvasFor(id));
      texture.colorSpace = SRGBColorSpace;
      texture.minFilter = LinearFilter;
      texture.magFilter = LinearFilter;
      /*
       * No mip chain, deliberately, and therefore no anisotropy either — the `anisotropy = 8`
       * that used to sit here was a dead assignment, because anisotropic filtering only ever
       * samples a mip chain that does not exist.
       *
       * Mipmaps are the wrong trade for these specifically: the faces are redrawn at 30 Hz
       * (`INSTRUMENT_REFRESH_HZ`) and every `needsUpdate` would rebuild the whole chain. The
       * cheaper answer to "the gauges look soft" is to stop minifying them — see `FACE_PX` in
       * `instruments.ts`, now large enough that a panel gauge is magnified rather than reduced at
       * a normal viewing distance.
       */
      texture.generateMipmaps = false;
      this.textures.set(id, texture);

      const faceMaterial = this.trackMaterial(new MeshBasicNodeMaterial());
      faceMaterial.map = texture;
      faceMaterial.toneMapped = true;
      this.gaugeMaterials.push(faceMaterial);

      // In the panel's own plane, so the whole six-pack sits proud of a tilted surface by the
      // same 2 mm wherever it is on the panel.
      const holder = new Group();
      holder.position.set(layout.x, layout.y, 0.012);
      this.panelFace.add(holder);
      this.gaugeHolders.set(id, holder);

      /*
       * A disc, not a quad.
       *
       * The face canvas is transparent outside its bezel, but an opaque quad shows the corners as
       * black squares behind the round instrument — which is exactly what it looked like. A
       * `CircleGeometry`'s UVs span its bounding square, so the texture maps unchanged and the
       * corners simply are not there. Cheaper than an alpha-tested quad, too.
       */
      const face = new Mesh(this.track(new CircleGeometry(layout.sizeM * 0.5, 28)), faceMaterial);
      face.position.z = 0.004;
      holder.add(face);

      // A metal bezel ring, so the instrument sits *in* the panel rather than on it.
      const bezel = new Mesh(
        this.track(new TorusGeometry(layout.sizeM * 0.47, layout.sizeM * 0.035, 6, 28)),
        bezelMaterial,
      );
      bezel.position.z = 0.006;
      holder.add(bezel);

      /*
       * The glass sheen: an additive quad with a soft vertical gradient.
       *
       * Additive rather than blended, because a reflection *adds* light to what is behind it —
       * blended glass would darken the numerals it is supposed to be sitting in front of, which
       * is the exact opposite of what glass does.
       */
      const glassMaterial = this.trackMaterial(new MeshBasicNodeMaterial());
      glassMaterial.map = sheenTexture();
      glassMaterial.transparent = true;
      glassMaterial.depthWrite = false;
      glassMaterial.opacity = 0.16;
      glassMaterial.toneMapped = false;
      this.glassMaterials.push(glassMaterial);

      const glass = new Mesh(this.track(new CircleGeometry(layout.sizeM * 0.46, 24)), glassMaterial);
      glass.position.z = 0.0075;
      holder.add(glass);
    }
  }

  /** Whichever control this aeroplane is flown with. */
  private buildYoke(): void {
    const material = this.litMaterial(false);
    material.color = new Color(0x16191d);
    material.roughness = 0.55;
    material.metalness = 0.15;

    if (this.profile.control === 'stick') {
      this.buildCentreStick(material);
      return;
    }

    const column = new Mesh(this.track(new CylinderGeometry(0.014, 0.016, 0.30, 10)), material);
    column.rotation.x = Math.PI / 2;
    column.position.set(0, 0, 0.06);
    this.yoke.add(column);

    // The wheel and its horns turn with the ailerons; the column does not.
    const rim = new Mesh(this.track(new TorusGeometry(0.072, 0.008, 6, 22, Math.PI * 1.25)), material);
    rim.rotation.z = Math.PI * 0.375;
    this.yokeWheel.add(rim);
    for (const side of [-1, 1]) {
      const horn = new Mesh(this.track(new CylinderGeometry(0.009, 0.009, 0.055, 8)), material);
      horn.rotation.z = Math.PI / 2;
      horn.position.set(side * 0.072, -0.028, 0);
      this.yokeWheel.add(horn);
    }
    const hub = new Mesh(this.track(new CylinderGeometry(0.022, 0.022, 0.016, 10)), material);
    hub.rotation.x = Math.PI / 2;
    this.yokeWheel.add(hub);

    this.yokeWheel.position.set(0, 0, -0.09);
    this.yoke.add(this.yokeWheel);
    // Left seat: the yoke is offset to the pilot's side rather than on the centreline.
    this.yoke.position.set(-0.02, -0.085, -0.52);
    this.structure.add(this.yoke);
  }

  /**
   * A centre stick: a column up from the floor between the pilot's knees, with a grip on top.
   *
   * The geometry is a third of the yoke's and it reads as far more correct on the two aeroplanes
   * that have one, because a yoke is *large and central* — on the fighter it filled the bottom of
   * the view with a Cessna's control wheel.
   *
   * It pivots at its base rather than translating, which is the real difference in how the two
   * controls move: a yoke slides in and out of the panel and its wheel spins, whereas a stick
   * tilts as one piece — aft for nose-up, left for left roll. `updateFrame` drives that through
   * the same two groups, so nothing downstream knows which is fitted.
   */
  private buildCentreStick(material: MeshStandardNodeMaterial): void {
    const column = new Mesh(this.track(new CylinderGeometry(0.011, 0.015, 0.26, 10)), material);
    // Modelled from the pivot up, so the group's origin is the pivot and rotating it is correct.
    column.position.set(0, 0.13, 0);
    this.yoke.add(column);

    const gripMaterial = this.litMaterial(false);
    gripMaterial.color = new Color(0x0d0f12);
    gripMaterial.roughness = 0.85;
    gripMaterial.metalness = 0.02;

    // A moulded grip, canted forward the way a hand actually holds one.
    const grip = new Mesh(this.track(new CylinderGeometry(0.021, 0.024, 0.10, 12)), gripMaterial);
    grip.position.set(0, 0.30, -0.012);
    grip.rotation.x = -8 * DEG2RAD;
    this.yoke.add(grip);

    // The trigger guard and the coolie hat: the two shapes that say "fighter" at a glance.
    const hat = new Mesh(this.track(new CylinderGeometry(0.008, 0.008, 0.008, 8)), material);
    hat.position.set(0, 0.352, -0.014);
    this.yoke.add(hat);
    const trigger = new Mesh(this.track(new BoxGeometry(0.012, 0.026, 0.008)), material);
    trigger.position.set(0, 0.30, -0.034);
    this.yoke.add(trigger);

    const boot = new Mesh(this.track(new CylinderGeometry(0.030, 0.038, 0.035, 12)), gripMaterial);
    boot.position.set(0, 0.017, 0);
    this.yoke.add(boot);

    /*
     * On the centreline and low, between the knees — not offset to the left seat like the yoke.
     * Far enough forward that the grip sits under the pilot's hand rather than in their lap, and
     * low enough that it does not cross the six-pack.
     */
    this.yoke.position.set(0, -0.30, -0.34);
    this.structure.add(this.yoke);
  }

  /** Throttle, mixture and flap levers, each on its own quadrant. */
  private buildLevers(): void {
    const quadrantMaterial = this.litMaterial(false);
    quadrantMaterial.color = new Color(0x1a1d21);
    quadrantMaterial.roughness = 0.8;

    const quadrant = new Mesh(this.track(new BoxGeometry(0.13, 0.075, 0.015)), quadrantMaterial);
    quadrant.position.set(0.10, -0.255, -0.60);
    quadrant.rotation.x = -PANEL_TILT_DEG * DEG2RAD;
    this.structure.add(quadrant);

    // Colours are the certificated ones: throttle black, mixture red, flaps white. A pilot
    // reaching without looking finds the right one by shape and colour, so they are not a choice.
    const makeLever = (group: Group, x: number, colourHex: number, knobRadius: number): void => {
      const material = this.litMaterial(false);
      material.color = new Color(colourHex);
      material.roughness = 0.45;

      const stem = new Mesh(this.track(new CylinderGeometry(0.005, 0.005, 0.055, 8)), material);
      stem.position.y = 0.027;
      group.add(stem);
      const knob = new Mesh(this.track(new CylinderGeometry(knobRadius, knobRadius, 0.012, 10)), material);
      knob.position.y = 0.058;
      group.add(knob);

      group.position.set(x, -0.255, -0.588);
      this.structure.add(group);
    };

    makeLever(this.throttleLever, 0.065, 0x101215, 0.014);
    makeLever(this.mixtureLever, 0.10, 0xb02015, 0.011);
    // The flap handle lives on the far side of the quadrant, where a 172's does.
    makeLever(this.flapLever, 0.175, 0xdfe3e8, 0.012);
  }

  /** Rudder pedals — visible below the panel, and they move. */
  private buildPedals(): void {
    const material = this.litMaterial(false);
    material.color = new Color(0x1e2226);
    material.roughness = 0.85;

    for (const [group, side] of [
      [this.pedalLeft, -1],
      [this.pedalRight, 1],
    ] as const) {
      const pedal = new Mesh(this.track(new BoxGeometry(0.075, 0.10, 0.012)), material);
      pedal.rotation.x = -68 * DEG2RAD;
      group.add(pedal);
      group.position.set(side * 0.10, -0.44, -0.70);
      this.structure.add(group);
    }
  }

  /**
   * Move the controls and refresh the instruments.
   *
   * @param nowMs             `performance.now()`, for the instruments' own 30 Hz clock.
   * @param controls          what the pilot and the assist are commanding.
   * @param instrumentInputs  the gauge readings, from `readInstruments`.
   * @param sunElevationDeg   drives the flood light and the instruments' internal lighting.
   * @param exposure          the scene's current exposure multiplier — see below.
   * @param dtSeconds         real frame time, for the control smoothing.
   */
  updateFrame(
    nowMs: number,
    controls: CockpitControls,
    instrumentInputs: InstrumentInputs,
    sunElevationDeg: number,
    exposure: number,
    dtSeconds: number,
  ): void {
    // Hidden means hidden: nothing below this line runs in an external view, which is what keeps
    // the cockpit's cost exactly zero when it is not being looked at.
    if (!this.visibleValue) return;

    // --- controls ----------------------------------------------------------------------------
    // A short half-life: the yoke follows the command closely, but a keyboard step still arrives
    // as a movement rather than a teleport.
    const halfLife = 0.05;
    this.smoothPitch = expDamp(this.smoothPitch, clamp(controls.pitch, -1, 1), halfLife, dtSeconds);
    this.smoothRoll = expDamp(this.smoothRoll, clamp(controls.roll, -1, 1), halfLife, dtSeconds);
    this.smoothYaw = expDamp(this.smoothYaw, clamp(controls.yaw, -1, 1), halfLife, dtSeconds);

    if (this.profile.control === 'stick') {
      /*
       * A stick tilts as one piece about its base: aft for nose-up, into the turn for roll.
       *
       * Nose up is the grip coming back toward the pilot. The column points up (+Y), so pulling
       * the top toward +Z is a *positive* rotation about X. Roll left drops the left wingtip, and
       * the grip goes with it — a negative rotation about Z moves the top toward −X.
       *
       * ±14° and ±12° are about the throw a real centre stick has; more looks like a joystick on
       * a desk, less looks like nothing is happening.
       */
      this.yoke.rotation.x = this.smoothPitch * 14 * DEG2RAD;
      this.yoke.rotation.z = -this.smoothRoll * 12 * DEG2RAD;
    } else {
      // Nose up is the yoke coming *back* — toward the pilot, which is +Z in render space.
      this.yoke.position.z = -0.52 + this.smoothPitch * 0.055;
      // …and a real yoke's wheel turns about 90° each way.
      this.yokeWheel.rotation.z = -this.smoothRoll * 80 * DEG2RAD;
    }

    // Pedals: right rudder pushes the right pedal forward and the left one back.
    this.pedalLeft.position.z = -0.70 + this.smoothYaw * 0.035;
    this.pedalRight.position.z = -0.70 - this.smoothYaw * 0.035;

    // Levers travel about 6 cm from idle to full.
    this.throttleLever.position.z = -0.588 - clamp(controls.throttle, 0, 1) * 0.058;
    this.mixtureLever.position.z = -0.588 - clamp(controls.mixture, 0, 1) * 0.058;
    const flapFraction = controls.maxFlapDeg > 0 ? controls.flapAngleDeg / controls.maxFlapDeg : 0;
    // The flap lever moves the other way — down is flaps down, which is how the handle works.
    this.flapLever.position.y = -0.255 - clamp(flapFraction, 0, 1) * 0.05;

    // --- night lighting ------------------------------------------------------------------------
    /*
     * The flood comes up through dusk rather than switching on.
     *
     * Between +4° and −6° of sun elevation, which is civil twilight — exactly when a pilot
     * reaches for the panel lights, and slow enough that nothing pops.
     */
    const target = clamp(
      (FLOOD_OFF_AT_DEG - sunElevationDeg) / (FLOOD_OFF_AT_DEG - FLOOD_FULL_AT_DEG),
      0,
      1,
    );
    this.floodAmount = expDamp(this.floodAmount, target, 0.8, dtSeconds);

    /*
     * **Divided by the exposure**, and that is the whole trick.
     *
     * OpenSkies's auto-exposure is driven by the atmosphere model, so at night it climbs to make a
     * moonlit landscape visible — up to its 48× ceiling. A panel light with a fixed intensity is
     * then multiplied by that same 48 and blows out to a flat saturated red; measured, that is
     * exactly what it did. Dividing by the exposure holds the panel's *rendered* brightness
     * constant however dark the world outside is, which is also the physically honest answer: a
     * pilot sets the panel lights to be just readable and their eye adapts, so the perceived
     * brightness of an instrument at night is roughly constant no matter what.
     */
    const adaptation = 1 / Math.max(0.05, exposure);
    // A red flood set to be *just* readable is a fraction of a sunlit panel, and the numbers
    // below are what a full-night exposure of ~12 produced.
    this.floodLight.intensity = this.floodAmount * DAY_PANEL_LUMINANCE * adaptation;

    /*
     * Instrument internal lighting.
     *
     * In daylight the faces are simply bright. At night they are lit from within, dimly, and
     * *warm* — post-lit instruments use the same red-orange as the flood so a pilot's night
     * vision survives looking at them. The face texture is multiplied by that colour, which is
     * why the material is unlit: there is no lighting calculation to modulate, only a tint.
     */
    const day = 1 - this.floodAmount;
    const r = day + this.floodAmount * 0.62;
    const g = day + this.floodAmount * 0.16;
    const b = day + this.floodAmount * 0.10;
    /*
     * **Both terms are exposure-referred, and until v1.1 only one of them was.**
     *
     * This read `day + floodAmount * 2.6 * adaptation`, so the *daylight* term went into the HDR
     * buffer as a bare 1 and was then multiplied by the scene exposure like everything else. The
     * faces are an unlit `MeshBasicNodeMaterial`, so there is no lighting calculation to hold them
     * down — at an exposure of 1 that is fine and it is why nobody noticed at noon, but the
     * auto-exposure climbs to a 48× ceiling as the world darkens. Measured on the shipped
     * `screenshots/lowi-dusk-approach.png`: the eight faces came out at sRGB **243–254** against
     * **53** on the panel slab five centimetres away — saturated white blobs with bloom halos,
     * completely unreadable, in the one shot whose whole job was to show approach lighting.
     *
     * Multiplying the whole expression by `adaptation` makes the rendered brightness
     * `luminance × adaptation × exposure = luminance`, i.e. constant against the world outside.
     * That is also the physically honest answer: a pilot's eye adapts, so an instrument's
     * perceived brightness barely changes between noon and midnight — which is exactly what the
     * flood term already assumed for itself.
     */
    const brightness = (day * DAY_PANEL_LUMINANCE + this.floodAmount * FLOOD_FACE_GAIN) * adaptation;
    for (const material of this.gaugeMaterials) {
      material.color.setRGB(r * brightness, g * brightness, b * brightness);
    }
    // The glass sheen is a reflection of the cabin, so it goes away when the cabin is dark.
    for (const material of this.glassMaterials) {
      material.opacity = 0.16 * day + 0.03 * this.floodAmount;
    }

    // --- instruments ----------------------------------------------------------------------------
    if (this.instruments.updateFrame(nowMs, instrumentInputs)) {
      // Only mark the textures dirty on a frame that actually redrew: a `needsUpdate` costs a
      // full texture re-upload, and eight of those at 120 fps for no change would dwarf the
      // drawing.
      for (const texture of this.textures.values()) texture.needsUpdate = true;
    }
    void this.inputs;
  }

  /** Snap the instruments and the controls, e.g. on a respawn. */
  snap(instrumentInputs: InstrumentInputs): void {
    this.instruments.snap(instrumentInputs);
    for (const texture of this.textures.values()) texture.needsUpdate = true;
    this.smoothPitch = 0;
    this.smoothRoll = 0;
    this.smoothYaw = 0;
  }

  dispose(): void {
    for (const texture of this.textures.values()) texture.dispose();
    // The photographed maps are cached and shared across cockpits, so the *source* is not disposed
    // here — only this cockpit's clones, each of which is its own GPU upload.
    for (const texture of this.textureSwaps) texture.dispose();
    this.environmentMap.dispose();
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    if (this.model !== null) disposeCockpitModel(this.model);
    sheenCache?.dispose();
    sheenCache = null;
  }
}

/**
 * The glass sheen, as a tiny gradient texture.
 *
 * One 4×64 strip, generated once and shared by all eight instruments — the gradient is vertical,
 * so it needs no horizontal resolution at all, and a shared texture means the glass costs one
 * upload rather than eight.
 */
let sheenCache: CanvasTexture | null = null;
function sheenTexture(): CanvasTexture {
  if (sheenCache !== null) return sheenCache;
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('2D canvas context unavailable — cockpit glass cannot be drawn');
  const gradient = ctx.createLinearGradient(0, 0, 0, 64);
  // Brightest across the top third, where light from the windscreen actually lands, and gone by
  // the middle. A symmetric gradient reads as a lens flare rather than as glass.
  gradient.addColorStop(0, 'rgba(210, 228, 255, 0.95)');
  gradient.addColorStop(0.32, 'rgba(170, 195, 230, 0.35)');
  gradient.addColorStop(0.55, 'rgba(120, 150, 190, 0.05)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 4, 64);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  sheenCache = texture;
  return texture;
}
